import { queryOptions } from '@tanstack/react-query';

import { apiGet, apiPost, type ApiError, type QueryOf, type ResponseOf } from './http';
import { queryKeys } from './queryKeys';

/**
 * The candidate table's route and the three status actions.
 *
 * `GET /api/v1/considerations/` is **a table of considerations, not of people** (`DESIGN.md` §6.3):
 * one person considered for two vacancies is two rows carrying the same `person_id`, which is ADR
 * 0002's split made visible rather than a duplicate to collapse.
 *
 * Everything here goes through `http.ts`'s `apiGet`/`apiPost` over the one `apiClient` — no second
 * axios instance — and every request and response type is derived from `schema.d.ts`. The only
 * shapes written by hand are the two `Omit<…> & { … }` narrowings below, which exist precisely
 * because the document is looser than the product can safely render.
 */

/** `?status=` (repeatable), `?role_id=`, `?search=`, `?page=`, `?page_size=`, read off the schema. */
export type ConsiderationListQuery = NonNullable<QueryOf<'/api/v1/considerations/', 'get'>>;

type ConsiderationPageFromApi = ResponseOf<'/api/v1/considerations/', 'get'>;
type ConsiderationRowFromApi = ConsiderationPageFromApi['items'][number];

/**
 * The row as the screens may treat it — **the payload itself** since backend TASK-104.
 *
 * ⚠️ It was `Omit<…> & { latest_recommendation: Recommendation | null }` because the document typed the
 * field `string | null`, and *"this is where a `\"MAYBE\"` stops, once, instead of throwing inside
 * `StatusDot`"*. The stop is now at the API: the field is the declared set of four or `null`, so a
 * `"MAYBE"` cannot be sent at all rather than being caught one layer in.
 */
export type ConsiderationRow = ConsiderationRowFromApi;

export type ConsiderationPage = ConsiderationPageFromApi;

/**
 * The API applies **no** default filter by design — §6.3's active-only default is the screen's — so
 * this helper sends exactly what it is given and nothing more.
 *
 * `average_score` and `latest_recommendation` are blind-filtered per caller (ADR 0006, backend
 * TASK-033) and are rendered exactly as sent: no aggregate is computed here and no second request
 * is issued to fill one in.
 */
export async function fetchConsiderations(
  query: ConsiderationListQuery = {},
): Promise<ConsiderationPage> {
  // The page as sent: the verdict is the API's declared set since backend TASK-104, so there is
  // nothing left for this layer to narrow.
  return apiGet('/api/v1/considerations/', { query });
}

/**
 * ⚠️ **The viewer is in the key, and no placeholder may reach around it.**
 *
 * The payload is viewer-dependent (ADR 0006), so `queryKeys.considerations` carries the viewer's id
 * and two viewers hold two entries for the one URL. Sprint 8's TASK-058 shipped a correct key and
 * then walked around it with `placeholderData: keepPreviousData`, which resolves from the
 * **observer's** last payload under any key and reports `status: 'success'` — so a viewer change
 * under a mounted screen rendered the previous viewer's blind-filtered figures. Do not add a
 * placeholder here; `candidates.test.tsx` swaps the viewer under a mounted list and goes red if one
 * comes back.
 */
export function considerationsQuery(viewerId: number, query: ConsiderationListQuery = {}) {
  /*
   * The error type is named because every failure of this route is an `ApiError` — `apiGet` converts
   * anything else before it leaves `http.ts` — and `ApiErrorState` is what decides which §8 state a
   * failure is. Without it a screen would hold a bare `Error` and have to cast at the very point the
   * offline/forbidden/throttle distinction is made (TASK-062).
   */
  return queryOptions<ConsiderationPage, ApiError>({
    queryKey: queryKeys.considerations(viewerId, query),
    queryFn: () => fetchConsiderations(query),
  });
}

/**
 * `ConsiderationRowOut` — what all three actions answer with: §6.3's row shape, and **none** of its
 * derived values (`average_score`, the two counters and `latest_recommendation` live on
 * `ConsiderationTableRowOut`, because a write path has no viewer's average to report). That is why a
 * caller refetches instead of writing this into either cache.
 */
export type ConsiderationAfterAction = ResponseOf<
  '/api/v1/considerations/{consideration_id}/make-offer/',
  'post'
>;

/**
 * Status moves through the three actions and never as a field (ADR 0010): there is no `status` in
 * any body below, because **these routes take no body at all**. Sending one would be the first sign
 * that status-as-a-field has crept back in.
 *
 * ⚠️ **Nothing in the type layer stops one being added**, which is worth knowing before trusting it:
 * these operations declare no `requestBody`, so `BodyOf` widens rather than refusing, and
 * `apiPost(path, { status: 'OFFER' }, …)` compiles. What fails is a **test** — `candidates.test.tsx`
 * calls **all three** against a recording adapter and asserts axios was handed `undefined`, verified
 * by adding `{ status: 'HIRED' }` to `hire` below: `tsc` stays green and that suite goes red.
 *
 * ⚠️ **It covered one action of three until the sprint-end review** (`code-reviewer`, 2026-07-28).
 * `personCardWrites.test.tsx` reads the body of the `make-offer` click only — `hire` is clicked
 * nowhere in the repository and `reject`'s body was never read — so the reviewer's mutation on `hire`
 * left `tsc`, eslint and all 426 tests green. The guard now lives at this edge rather than behind a
 * button, because an action with no screen still has to be covered.
 *
 * They answer `ConsiderationRowOut` (`ConsiderationAfterAction`), which carries none of §6.3's
 * derived values — so a caller refetches the row and the card rather than writing the answer into
 * either cache. `tsc` refuses that write, which is what the two schemas are for.
 */
export const makeOffer = (considerationId: number) =>
  apiPost('/api/v1/considerations/{consideration_id}/make-offer/', undefined, {
    params: { consideration_id: considerationId },
  });

export const hire = (considerationId: number) =>
  apiPost('/api/v1/considerations/{consideration_id}/hire/', undefined, {
    params: { consideration_id: considerationId },
  });

export const reject = (considerationId: number) =>
  apiPost('/api/v1/considerations/{consideration_id}/reject/', undefined, {
    params: { consideration_id: considerationId },
  });

/*
 * ⚠️ **`Role`, `fetchRoles` and `rolesQuery` used to live here and now live in `vacancies.ts`
 * (TASK-070).** The condition for the move was written into their own docblock — *"it lives beside
 * the considerations rather than in a module of its own because those are its only two callers here;
 * the vacancy screens (§6.8) are not built"* — and Sprint 10 meets it. The reason for moving them the
 * moment it was met, rather than importing them from here as well, is the cache key: the catalogue is
 * paginated, `queryKeys.roles` had no query segment, and the first screen to ask for a second page
 * would have served it to §6.3's filter and §6.4's dialog. The two Sprint 9 call sites import from
 * `@/api/vacancies` now, and this note is here so the sentence above is not left reading as current.
 */
